import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { disconnectP2PStackThunk } from '@/store/thunks/poc/connection/disconnectP2PStackThunk';
import { disconnectPeerThunk } from '@/store/thunks/poc/connection/disconnectPeerThunk';
import { getConnectionInfoThunk } from '@/store/thunks/poc/connection/getConnectionInfoThunk';
import { initializeP2PStackThunk } from '@/store/thunks/poc/connection/initializeP2PStackThunk';
import { reconnectPeerThunk } from '@/store/thunks/poc/connection/reconnectPeerThunk';
import { extractThunkError } from '@/store/utils';
import type { ConnectionInfo } from '@/types/poc/libp2p';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SingleConnectionState {
  sixId: string;
  status: ConnectionStatus;
  peerId: string | null;
  connectedPeers: number;
  errorMessage: string | null;
  isP2PStackInitialized: boolean;
  subscribedTopics: string[];
  subscriberCounts: Record<string, number>;
  initializationProgress: {
    libp2p: boolean;
    helia: boolean;
    messaging: boolean;
  };
  connectionDetails: ConnectionInfo[];
}

export interface ConnectionState {
  connections: SingleConnectionState[];
}

export const createInitialConnection = (sixId: string): SingleConnectionState => ({
  sixId,
  status: 'disconnected',
  peerId: null,
  connectedPeers: 0,
  errorMessage: null,
  isP2PStackInitialized: false,
  subscribedTopics: [],
  subscriberCounts: {},
  initializationProgress: {
    libp2p: false,
    helia: false,
    messaging: false,
  },
  connectionDetails: [],
});

const initialState: ConnectionState = {
  connections: [],
};

const findConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState | undefined =>
  connections.find((connection) => connection.sixId === sixId);

const ensureConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState[] =>
  findConnection(connections, sixId) == null
    ? [...connections, createInitialConnection(sixId)]
    : connections;

const updateConnection = (
  connections: SingleConnectionState[],
  sixId: string,
  updater: (connection: SingleConnectionState) => SingleConnectionState
): SingleConnectionState[] =>
  connections.map((connection) =>
    connection.sixId === sixId ? updater(connection) : connection
  );

const getStatusFromConnections = (
  connectionDetails: ConnectionInfo[],
  isP2PStackInitialized: boolean
): ConnectionStatus => {
  if (!isP2PStackInitialized) {
    return 'disconnected';
  }

  return connectionDetails.some((connection) => connection.isRelay)
    ? 'connected'
    : 'connecting';
};

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    updateConnectionDetails: (
      state,
      action: PayloadAction<{ sixId: string; connections: ConnectionInfo[] }>
    ) => {
      state.connections = ensureConnection(state.connections, action.payload.sixId);
      state.connections = updateConnection(
        state.connections,
        action.payload.sixId,
        (connection) => ({
          ...connection,
          connectionDetails: action.payload.connections,
          connectedPeers: action.payload.connections.length,
          status: getStatusFromConnections(
            action.payload.connections,
            connection.isP2PStackInitialized
          ),
        })
      );
    },
    setConnectionError: (
      state,
      action: PayloadAction<{ sixId: string; errorMessage: string | null }>
    ) => {
      state.connections = ensureConnection(state.connections, action.payload.sixId);
      state.connections = updateConnection(
        state.connections,
        action.payload.sixId,
        (connection) => ({
          ...connection,
          errorMessage: action.payload.errorMessage,
          status: action.payload.errorMessage == null ? connection.status : 'error',
        })
      );
    },
    updateTopicSubscriberCounts: (
      state,
      action: PayloadAction<{ sixId: string; subscriberCounts: Record<string, number> }>
    ) => {
      state.connections = ensureConnection(state.connections, action.payload.sixId);
      state.connections = updateConnection(
        state.connections,
        action.payload.sixId,
        (connection) => ({
          ...connection,
          subscriberCounts: action.payload.subscriberCounts,
        })
      );
    },
    removeSixConnection: (state, action: PayloadAction<string>) => {
      state.connections = state.connections.filter(
        (connection) => connection.sixId !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeP2PStackThunk.pending, (state, action) => {
        const { sixId } = action.meta.arg;
        state.connections = ensureConnection(state.connections, sixId);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          status: 'connecting',
          errorMessage: null,
        }));
      })
      .addCase(initializeP2PStackThunk.fulfilled, (state, action) => {
        state.connections = updateConnection(
          state.connections,
          action.payload.sixId,
          (connection) => ({
            ...connection,
            peerId: action.payload.peerId,
            connectedPeers: action.payload.peers,
            errorMessage: null,
            isP2PStackInitialized: true,
            subscribedTopics: action.payload.subscribedTopics,
            initializationProgress: {
              libp2p: true,
              helia: true,
              messaging: action.payload.messagingInitialized,
            },
            status: getStatusFromConnections(connection.connectionDetails, true),
          })
        );
      })
      .addCase(initializeP2PStackThunk.rejected, (state, action) => {
        const { sixId } = action.meta.arg;
        const { message } = extractThunkError(action.payload);
        state.connections = ensureConnection(state.connections, sixId);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          status: 'error',
          errorMessage: message ?? action.error.message ?? 'Unknown error',
          isP2PStackInitialized: false,
          initializationProgress: {
            libp2p: false,
            helia: false,
            messaging: false,
          },
        }));
      })
      .addCase(disconnectP2PStackThunk.fulfilled, (state, action) => {
        state.connections = updateConnection(
          state.connections,
          action.payload.sixId,
          () => createInitialConnection(action.payload.sixId)
        );
      })
      .addCase(disconnectP2PStackThunk.rejected, (state, action) => {
        const { sixId } = action.meta.arg;
        const { message } = extractThunkError(action.payload);
        state.connections = ensureConnection(state.connections, sixId);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          status: 'error',
          errorMessage: message ?? action.error.message ?? 'Unknown error',
        }));
      })
      .addCase(getConnectionInfoThunk.fulfilled, (state, action) => {
        state.connections = ensureConnection(state.connections, action.payload.sixId);
        state.connections = updateConnection(
          state.connections,
          action.payload.sixId,
          (connection) => ({
            ...connection,
            connectionDetails: action.payload.connections,
            connectedPeers: action.payload.connections.length,
            status: getStatusFromConnections(
              action.payload.connections,
              connection.isP2PStackInitialized
            ),
          })
        );
      })
      .addCase(disconnectPeerThunk.rejected, (state, action) => {
        const { sixId } = action.meta.arg;
        const { message } = extractThunkError(action.payload);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          errorMessage: message ?? action.error.message ?? 'Unknown error',
        }));
      })
      .addCase(reconnectPeerThunk.rejected, (state, action) => {
        const { sixId } = action.meta.arg;
        const { message } = extractThunkError(action.payload);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          errorMessage: message ?? action.error.message ?? 'Unknown error',
        }));
      });
  },
});

export const {
  updateConnectionDetails,
  updateTopicSubscriberCounts,
  setConnectionError,
  removeSixConnection,
} = connectionSlice.actions;

export default connectionSlice.reducer;
